import { useCallback, useEffect, useState } from "react";
import { Loader2, Check, X, RefreshCw } from "lucide-react";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import { toast } from "../../../components/ui/sonner";

const STATUSES = ["PENDING", "APPROVED", "REJECTED", ""];

function looksLikeImage(v) {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s) return false;
  if (s.startsWith("data:image/")) return true;
  if (/^https?:\/\/[^\s]+\.(jpe?g|png|gif|webp|svg|bmp|heic|heif)(\?[^\s]*)?$/i.test(s)) return true;
  if (s.length > 200 && /^[A-Za-z0-9+/=_-]+$/.test(s) && !s.includes(" ")) return true;
  return false;
}

function toSrc(v) {
  const s = String(v).trim();
  if (s.startsWith("data:image/") || /^https?:\/\//i.test(s)) return s;
  return `data:image/jpeg;base64,${s}`;
}

export default function KycTab() {
  const { authFetch } = useAdminAuth();
  const [status, setStatus] = useState("PENDING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = useCallback(
    async (st = status) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (st) params.set("status", st);
        params.set("limit", "100");
        const data = await authFetch(`/api/admin/kyc?${params.toString()}`);
        setItems(data.items || []);
      } catch {
        /* handled */
      } finally {
        setLoading(false);
      }
    },
    [authFetch, status]
  );

  useEffect(() => {
    load(status);
  }, [status, load]);

  const act = async (id, action, note = "") => {
    setActing(id + action);
    try {
      const res = await authFetch(`/api/admin/kyc/${action}`, {
        method: "POST",
        body: JSON.stringify({ kyc_id: id, note }),
      });
      toast(`KYC ${res.status}`);
      load(status);
    } catch (e) {
      toast("Action failed", { description: e.message });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-full border border-white/10 bg-black/20 p-1">
          {STATUSES.map((s) => (
            <button
              key={s || "all"}
              data-testid={`kyc-filter-${(s || "all").toLowerCase()}`}
              onClick={() => setStatus(s)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors ${
                status === s ? "bg-[#00B4D8] text-[#04140a]" : "text-slate-300"
              }`}
            >
              {s || "all"}
            </button>
          ))}
        </div>
        <button onClick={() => load(status)} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#00B4D8]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="kyc-empty" className="text-slate-500 text-center py-16">
          No {status ? status.toLowerCase() : ""} KYCs.
        </p>
      ) : (
        <div className="space-y-4">
          {items.map((k) => (
            <div key={k.id} data-testid={`kyc-item-${k.id}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <div className="font-semibold text-white">{k.full_name || "—"}</div>
                  <div className="text-xs text-slate-500">{k.email || k.user_id}</div>
                </div>
                <span
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    k.status === "APPROVED"
                      ? "border-[#4CAF50]/40 text-[#4CAF50] bg-[#4CAF50]/10"
                      : k.status === "REJECTED"
                      ? "border-red-400/40 text-red-300 bg-red-400/10"
                      : "border-amber-300/40 text-amber-300 bg-amber-300/10"
                  }`}
                >
                  {k.status || "PENDING"}
                </span>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                {Object.entries(k)
                  .filter(([kk]) => !["id", "user_id", "status", "reviewed_at", "submitted_at", "admin_note"].includes(kk))
                  .map(([kk, vv]) => {
                    const isImg = looksLikeImage(vv);
                    return (
                      <div key={kk} className="flex flex-col gap-1">
                        <dt className="text-[10px] uppercase tracking-widest text-slate-500">{kk}</dt>
                        <dd className="text-slate-200 break-all">
                          {isImg ? (
                            <a href={toSrc(vv)} target="_blank" rel="noreferrer">
                              <img
                                src={toSrc(vv)}
                                alt={kk}
                                loading="lazy"
                                className="max-h-40 rounded-xl border border-white/10 object-cover"
                              />
                            </a>
                          ) : (
                            String(vv)
                          )}
                        </dd>
                      </div>
                    );
                  })}
              </dl>

              {String(k.status || "").toUpperCase() === "PENDING" && (
                <div className="flex gap-2 justify-end">
                  <button
                    data-testid={`kyc-reject-${k.id}`}
                    onClick={() => act(k.id, "reject")}
                    disabled={acting === k.id + "reject"}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-400/50 text-red-300 px-4 py-2 text-xs font-semibold hover:bg-red-400/10 disabled:opacity-60"
                  >
                    <X size={13} /> Reject
                  </button>
                  <button
                    data-testid={`kyc-approve-${k.id}`}
                    onClick={() => act(k.id, "approve")}
                    disabled={acting === k.id + "approve"}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#4CAF50] px-4 py-2 text-xs font-semibold text-[#04140a] hover:brightness-110 disabled:opacity-60"
                  >
                    <Check size={13} /> Approve
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
